import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactFlow, MiniMap, Controls, Background, applyNodeChanges } from '@xyflow/react';
import { Modal, Button } from 'antd';
import { useStore } from '../../../../store/rootStore';
import CustomNode from './CustomNode';
import LoopNode from './LoopNode';
import styles from '../RunDetailView.module.scss';
const nodeTypes = {
    custom: CustomNode,
    loop: LoopNode,
};
const RunStepsGraph = ({ steps }) => {
    const [isInteractive, setIsInteractive] = useState(true);
    const { scenarios: scenariosStore } = useStore();
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [selectedIteration, setSelectedIteration] = useState(null);
    const { t } = useTranslation();
    const onViewIteration = useCallback((iter, index) => {
        setSelectedIteration({ iter, index });
    }, []);
    const getTitle = useMemo(() => (step) => {
        var _a;
        const { name } = step;
        const storeMap = {
            event: scenariosStore.events,
            loader: scenariosStore.loaders,
            condition: scenariosStore.conditions,
            action: scenariosStore.actions,
        };
        const componentDefinitions = Object.values((_a = storeMap[step.type]) !== null && _a !== void 0 ? _a : {});
        const componentDef = componentDefinitions.find((c) => c.name === name);
        return componentDef === null || componentDef === void 0 ? void 0 : componentDef.title;
    }, [scenariosStore]);
    useEffect(() => {
        const generatedNodes = [];
        const generatedEdges = [];
        steps.forEach((step, index) => {
            const nodeId = `step-${index}`;
            if (step.type === 'loop') {
                generatedNodes.push({
                    id: nodeId,
                    type: 'loop',
                    position: { x: index * 500, y: 100 },
                    data: {
                        step,
                        isFirst: index === 0,
                        isLast: index === steps.length - 1,
                        onViewIteration,
                    },
                });
            }
            else {
                const title = getTitle(step);
                generatedNodes.push({
                    id: nodeId,
                    type: 'custom',
                    position: { x: index * 500, y: 100 },
                    data: {
                        step,
                        isFirst: index === 0,
                        isLast: index === steps.length - 1,
                        title,
                        isInteractive,
                    },
                });
            }
            if (index > 0) {
                generatedEdges.push({
                    id: `e-step-${index - 1}-step-${index}`,
                    source: `step-${index - 1}`,
                    target: nodeId,
                    sourceHandle: 'source',
                    targetHandle: 'target',
                    animated: true,
                });
            }
        });
        setNodes(generatedNodes);
        setEdges(generatedEdges);
    }, [steps, getTitle, isInteractive, onViewIteration]);
    const onNodesChange = useCallback((changes) => {
        const safeChanges = changes.filter((c) => c.type !== 'remove');
        if (safeChanges.length > 0) {
            setNodes((nds) => applyNodeChanges(safeChanges, nds));
        }
    }, []);
    if (!steps.length) {
        return React.createElement("div", { style: { padding: '20px' } },
            React.createElement(Trans, { i18nKey: "runNoStepsToDisplay" }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.runStepsGraphContainer },
            React.createElement(ReactFlow, { nodes: nodes, edges: edges, onNodesChange: onNodesChange, nodeTypes: nodeTypes, fitView: true, nodesDraggable: isInteractive, nodesConnectable: isInteractive, elementsSelectable: isInteractive, zoomOnScroll: isInteractive, panOnDrag: isInteractive, zoomOnDoubleClick: isInteractive, panOnScroll: isInteractive, zoomOnPinch: isInteractive },
                React.createElement(MiniMap, null),
                React.createElement(Controls, { onInteractiveChange: setIsInteractive }),
                React.createElement(Background, null))),
        React.createElement(Modal, { title: selectedIteration !== null ? t('runIterationModalTitle', { index: selectedIteration.index + 1 }) : '', open: selectedIteration !== null, onOk: () => setSelectedIteration(null), onCancel: () => setSelectedIteration(null), width: "90vw", bodyStyle: { height: '70vh', padding: 0 }, footer: [
                React.createElement(Button, { key: "close", onClick: () => setSelectedIteration(null) },
                    React.createElement(Trans, { i18nKey: "close", defaults: "Close" })),
            ], destroyOnClose: true }, selectedIteration !== null && (React.createElement(RunStepsGraph, { steps: selectedIteration.iter.steps || [] })))));
};
export default RunStepsGraph;
